// /dashboard: signed-in home — the next step, the machine, the apps and what runs on it. The plan, devices and settings live on /account.

import { lazy, Suspense, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { changePassword, createMachine, deleteAccount, machineState } from "../api";
import { Link, navigate } from "../router";
import { PlanBanner } from "./PlanCard";
import { refreshMe } from "../session";
import { useStore, type Me } from "../store";
import { LINK_CHOICES, setLinksSetting } from "../ui/openLinkStore";
import { LinkArt } from "../ui/LinkArt";
import { DashLayout } from "./Shell";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { MachineArt } from "./MachineArt";
import { MachineHome } from "./MachineHome";
import { GetStartedCard, SetupHero, setupOpen, useNotNow } from "./GetStarted";
import { TierPicker, useTierChoices } from "./MachineControls";
import { Toasts } from "../ui/Toasts";
import { PasswordInput } from "../ui/PasswordInput";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { AppsCard } from "../ui/GetApps";

// What runs on the machine; it brings the live connection along, so it loads on its own.
const ActivityCard = lazy(() => import("./Activity"));

type Setup = {
  phase:
    | "idle"
    | "starting"
    | "creating"
    | "booting"
    | "ready"
    | "error"
    | "full"
    | "unavailable";
  detail?: string;
  since?: number;
};

const STEPS: {
  key: "creating" | "booting" | "ready";
  label: string;
  hint: string;
}[] = [
  {
    key: "creating",
    label: "Creating your cloud machine",
    hint: "Copying a fresh Linux image with your tools",
  },
  {
    key: "booting",
    label: "Starting Linux",
    hint: "Booting, then starting the cmx agent",
  },
  {
    key: "ready",
    label: "Ready",
    hint: "Terminal, desktop and previews are on",
  },
];

/** "Fire up my cloud machine": create it, then follow it until the agent answers. */
function useMachineSetup(machine: Me["machine"]) {
  const [setup, setSetup] = useState<Setup>({ phase: "idle" });
  // A machine that isn't answering may still be coming up (e.g. after a reload mid-setup).
  useEffect(() => {
    if (!machine || machine.status === "running") return;
    void machineState().then((r) => {
      if (
        r.ok &&
        (r.data.machine.state === "creating" ||
          r.data.machine.state === "booting")
      )
        setSetup({ phase: r.data.machine.state, since: Date.now() });
    });
  }, [machine]);
  // An unreachable machine is looked at again until it answers.
  useEffect(() => {
    if (machine?.status !== "unreachable") return;
    const t = setInterval(() => void refreshMe(), 10_000);
    return () => clearInterval(t);
  }, [machine?.status]);
  useEffect(() => {
    if (setup.phase !== "creating" && setup.phase !== "booting") return;
    const t = setInterval(async () => {
      const r = await machineState();
      if (!r.ok) return;
      const st = r.data.machine.state;
      if (st === "ready") {
        setSetup((s) => ({ ...s, phase: "ready" }));
        void refreshMe();
      } else if (st === "error")
        setSetup((s) => ({
          ...s,
          phase: "error",
          detail: r.data.machine.detail,
        }));
      else if (st === "creating" || st === "booting")
        setSetup((s) => (s.phase === st ? s : { ...s, phase: st }));
    }, 2000);
    return () => clearInterval(t);
  }, [setup.phase]);
  const fireUp = async (tier?: string) => {
    setSetup({ phase: "starting", since: Date.now() });
    const r = await createMachine(tier);
    if (r.ok) {
      const st = r.data.machine.state;
      setSetup({
        phase:
          st === "ready" ? "ready" : st === "booting" ? "booting" : "creating",
        since: Date.now(),
      });
      if (st === "ready") void refreshMe();
      return;
    }
    if (r.status === 409) setSetup({ phase: "full", detail: r.error });
    else if (r.status === 503) setSetup({ phase: "unavailable" });
    else setSetup({ phase: "error", detail: r.error });
  };
  return { setup, fireUp };
}

function SetupProgress({ setup }: { setup: Setup }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const at = setup.phase === "ready" ? 2 : setup.phase === "booting" ? 1 : 0;
  const secs = setup.since
    ? Math.max(0, Math.round((Date.now() - setup.since) / 1000))
    : 0;
  return (
    <div className="setup" data-testid="machine-setup" data-phase={setup.phase}>
      <ol className="setup-steps">
        {STEPS.map((s, i) => (
          <li key={s.key} className={i < at ? "done" : i === at ? "now" : ""}>
            <span className="setup-dot" aria-hidden="true" />
            <div>
              <div className="setup-label">{s.label}</div>
              <div className="muted small">{s.hint}</div>
            </div>
          </li>
        ))}
      </ol>
      {setup.phase !== "ready" && (
        <p className="muted small" aria-live="polite">
          {secs}s · usually under 2 minutes. You can leave this page; it keeps
          going.
        </p>
      )}
    </div>
  );
}

function MachineCard({ me }: { me: Me }) {
  const machine = me.machine;
  const paid = me.billing.enabled;
  const { setup, fireUp } = useMachineSetup(machine);
  const inProgress =
    setup.phase === "starting" ||
    setup.phase === "creating" ||
    setup.phase === "booting";
  const { choices } = useTierChoices();
  const [tier, setTier] = useState("");
  useEffect(() => {
    if (choices && !tier) {
      const t =
        choices.tiers.find((x) => x.id === choices.default) ?? choices.tiers[0];
      if (t) setTier(t.id);
    }
  }, [choices, tier]);

  if (!machine || (inProgress && machine.status !== "running"))
    return (
      <section
        id="machine"
        className="card card-machine"
        aria-labelledby="machine-title"
        data-testid="no-machine"
      >
        <div className="card-head">
          <h2 id="machine-title">Your cloud machine</h2>
          <span className="pill pill-amber">
            <span className="dot dot-waiting" aria-hidden="true" />{" "}
            {inProgress ? "Setting up" : "Not started"}
          </span>
        </div>
        {inProgress || setup.phase === "ready" ? (
          <SetupProgress setup={setup} />
        ) : (
          <div className="waiting">
            <div className="waiting-art" aria-hidden="true">
              <span>&gt;_</span>
            </div>
            <div>
              {setup.phase === "full" ? (
                <>
                  <h3>All cloud machines are taken right now</h3>
                  <p className="muted" data-testid="machine-full">
                    You’re on the list: we’ll let you know as soon as one frees
                    up.
                  </p>
                </>
              ) : setup.phase === "unavailable" ? (
                <>
                  <h3>Waiting for a cloud machine</h3>
                  <p className="muted">
                    We’re setting aside a cloud machine for you. It shows up here as
                    soon as it’s assigned.
                  </p>
                </>
              ) : paid ? (
                <>
                  <h3>{me.billing.plan ? "Your cloud machine is on its way" : "Pick a plan for your cloud machine"}</h3>
                  <p className="muted">
                    {me.billing.plan
                      ? "Your plan is paid; we make your cloud machine as soon as there’s room. We’ll email you."
                      : "A Linux cloud machine that stays on for you, with Claude Code, Codex, Docker and a desktop with Chrome."}
                  </p>
                  {!me.billing.plan && (
                    <Link to="/plans" className="btn btn-primary btn-lg" data-testid="pick-plan">
                      Pick a plan <span aria-hidden="true">→</span>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  <h3>Fire up your cloud machine</h3>
                  <p className="muted">
                    A Linux cloud machine that stays on for you, with Claude Code,
                    Codex, Docker and a desktop with Chrome. Pick its size; you
                    can change it any time.
                  </p>
                  {choices && tier && (
                    <TierPicker choices={choices} tier={tier} onTier={setTier} />
                  )}
                  {setup.phase === "error" && (
                    <div
                      className="form-error"
                      role="alert"
                      data-testid="machine-error"
                    >
                      {setup.detail || "Something went wrong."} Try again, or
                      tell us if it keeps happening.
                    </div>
                  )}
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={() => void fireUp(tier || undefined)}
                    data-testid="fire-up"
                  >
                    Fire up my cloud machine <span aria-hidden="true">→</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    );

  return <MachineHome me={me} />;
}

export function PasswordForm({ email, bare }: { email: string; bare?: boolean }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setOk(false);
    if (!current) return setError("Enter your current password.");
    if (next.length < 10)
      return setError("New password must be at least 10 characters.");
    if (next !== confirm) return setError("The new passwords don’t match.");
    if (next === current)
      return setError(
        "The new password must be different from the current one.",
      );
    setBusy(true);
    setError(null);
    const r = await changePassword(current, next);
    setBusy(false);
    if (r.ok) {
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } else if (r.status === 403)
      setError("Your current password is incorrect.");
    else setError(r.error);
  };

  const describe = error ? "pw-error" : ok ? "pw-ok" : "pw-hint";
  return (
    <form
      className="form"
      onSubmit={submit}
      noValidate
      data-testid="password-form"
    >
      {!bare && <h3 className="form-title">Change password</h3>}
      {/* Hidden username helps password managers pair the new password with the account. */}
      <input
        type="email"
        autoComplete="username"
        value={email}
        readOnly
        hidden
      />
      <div className="field">
        <label htmlFor="pw-current">Current password</label>
        <PasswordInput
          id="pw-current"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          data-testid="pw-current"
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="pw-next">New password</label>
          <PasswordInput
            id="pw-next"
            autoComplete="new-password"
            minLength={10}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            aria-describedby={describe}
            data-testid="pw-next"
          />
        </div>
        <div className="field">
          <label htmlFor="pw-confirm">Confirm new password</label>
          <PasswordInput
            id="pw-confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={confirm.length > 0 && confirm !== next}
            data-testid="pw-confirm"
          />
        </div>
      </div>
      {error ? (
        <div
          className="form-error"
          id="pw-error"
          role="alert"
          data-testid="pw-error"
        >
          {error}
        </div>
      ) : ok ? (
        <div className="form-ok" id="pw-ok" role="status" data-testid="pw-ok">
          Password updated.
        </div>
      ) : (
        <div className="muted small-print" id="pw-hint">
          At least 10 characters.
        </div>
      )}
      <div>
        <Button type="submit" disabled={busy} data-testid="pw-submit">
          {busy ? "Saving…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}

/** Delete the account for good, typing its email to confirm (spec "Deleting an account"). */
export function DeleteAccount({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = typed.trim().toLowerCase() === me.email.toLowerCase();
  const close = () => (setOpen(false), setTyped(""), setError(null));
  // a dialog, as the Mac asks before anything it can't undo: what goes, the email to type, Cancel and Delete
  return (
    <>
      <Button variant="danger-ghost" onClick={() => setOpen(true)} data-testid="delete-account">
        Delete account
      </Button>
      {open && (
        <Dialog title="Delete your account?" onClose={close} testId="delete-account-dialog">
          <form
            className="dlg-form"
            onSubmit={async (e: FormEvent) => {
              e.preventDefault();
              if (!ok || busy) return;
              setBusy(true);
              setError(null);
              const r = await deleteAccount(typed.trim());
              if (!r.ok) {
                setBusy(false);
                return setError(r.error);
              }
              await refreshMe();
              navigate("/", { replace: true });
            }}
            data-testid="delete-account-form"
          >
            <p>
              This ends your plan at once and deletes {me.machine ? <>your cloud machine <b>{me.machine.id}</b>, everything on its disk,</> : "your account,"} your
              settings and every signed-in device. It can’t be undone.
            </p>
            <div className="field">
              <label htmlFor="delete-confirm">
                Type <b>{me.email}</b> to confirm
              </label>
              <input
                id="delete-confirm"
                type="email"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                data-testid="delete-account-confirm"
              />
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <div className="form-actions">
              <Button type="button" onClick={close}>
                Cancel
              </Button>
              <Button variant="danger" type="submit" disabled={!ok || busy} data-testid="delete-account-submit">
                {busy ? "Deleting…" : "Delete everything"}
              </Button>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

/** A setting as a card of choices (saved for all the person's devices). The picked one shows
 *  at once, while it saves. With art, the choices are cards side by side, each with its drawing. */
function SettingCard<T extends string>({ id, title, lead, choices, value, save, art }: {
  id: string;
  title: string;
  lead: ReactNode;
  choices: { value: T; label: string; hint: string }[];
  value: T;
  save: (v: T) => Promise<boolean>;
  art?: (v: T) => ReactNode;
}) {
  const [saving, setSaving] = useState<T | null>(null);
  const current = saving ?? value;
  return (
    <section className="card" aria-labelledby={`${id}-title`} data-testid={`${id}-card`}>
      <div className="card-head">
        <h2 id={`${id}-title`}>{title}</h2>
      </div>
      <p className="muted">{lead}</p>
      <div className={`choice-list${art ? " is-cards" : ""}`} role="radiogroup" aria-labelledby={`${id}-title`}>
        {choices.map((c) => (
          <label key={c.value} className="choice">
            {art && <span className="choice-art">{art(c.value)}</span>}
            <input
              type="radio"
              name={id}
              value={c.value}
              checked={current === c.value}
              disabled={saving !== null}
              data-testid={`${id}-${c.value}`}
              onChange={async () => {
                setSaving(c.value);
                await save(c.value);
                setSaving(null);
              }}
            />
            <span className="choice-dot" aria-hidden="true" />
            <span>
              <span className="choice-label">{c.label}</span>
              <span className="muted small-print">{c.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

/** "Open links from your cloud machine": where links programs on the cloud machine open. */
export function LinksCard({ me }: { me: Me }) {
  return (
    <SettingCard
      id="links"
      title="Open links from your cloud machine"
      lead={<>When a program on {me.machine?.id ?? "the cloud machine"} opens a link: a sign-in, a dev server, docs.</>}
      choices={LINK_CHOICES}
      value={me.settings.links}
      save={setLinksSetting}
      art={(v) => <LinkArt to={v} />}
    />
  );
}

/** The right of the onboarding hero: the machine drawn at the size it has, its status, and the account link. */
function MachineAside({ machine }: { machine: Me["machine"] }) {
  const ready = machine?.status === "running";
  const specs = machine
    ? [machine.vcpu && `${machine.vcpu} vCPU`, machine.memoryGB && `${machine.memoryGB} GB`, machine.diskGB && `${machine.diskGB} GB disk`].filter(Boolean)
    : [];
  return (
    <>
      <MachineArt cpu={machine?.vcpu} mem={machine?.memoryGB} sto={machine?.diskGB} />
      <div className="machine-ready" data-testid="machine-ready" data-ready={ready}>
        {ready ? <IconCircleCheckFilled size={18} className="machine-ready-icon is-ready" aria-hidden /> : <span className="loading-ring machine-ready-icon is-wait" aria-hidden />}
        <span className="machine-ready-text">
          <strong>
            {!machine
              ? "Your cloud machine is on its way"
              : ready
                ? `Your cloud machine ${machine.id} is ready`
                : `Your cloud machine ${machine.id} is starting`}
          </strong>
          {specs.length > 0 && <span className="muted">{specs.join(" · ")}</span>}
        </span>
      </div>
    </>
  );
}

/**
 * Until Magic Setup is answered (run, or Don't ask again), the dashboard is that one step, large,
 * with the machine on the hero's right (Account lives in the top bar); Not now shows everything for this
 * visit (spec "Dashboard: onboarding first").
 */
function Onboarding({ me }: { me: Me }) {
  return (
    <div className="dash-onboarding">
      <SetupHero me={me} aside={<MachineAside machine={me.machine} />} />
    </div>
  );
}

export function Dashboard() {
  const me = useStore((s) => s.me)!;
  const name = me.email.split("@")[0];
  const later = useNotNow("setup");
  if (setupOpen(me) && !later) {
    return (
      <DashLayout title={name ? `Hi, ${name}` : "Welcome"} subtitle="One step and your cloud machine feels like this computer.">
        <PlanBanner me={me} />
        <Onboarding me={me} />
        <Toasts />
      </DashLayout>
    );
  }
  // The machine and what runs on it; the plan, devices and settings live on /account.
  return (
    <DashLayout
      title={name ? `Hi, ${name}` : "Dashboard"}
      subtitle="Your cloud machine at a glance."
    >
      <PlanBanner me={me} />
      <div className="dash-grid">
        <GetStartedCard me={me} />
        <MachineCard me={me} />
        <AppsCard />
        {me.machine && (
          <Suspense fallback={null}>
            <ActivityCard />
          </Suspense>
        )}
      </div>
      <Toasts />
    </DashLayout>
  );
}
